/**
 * File: useLeadsQueries.ts
 * Path: apps/admin/src/shared/hooks/useLeadsQueries.ts
 * Description: React Query wrappers over the leads repository.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FIRESTORE_COLLECTIONS,
} from "@idk/config";
import { leadsRepo } from "../services/leadsService";

const leadsKey = [FIRESTORE_COLLECTIONS.leads] as const;
export const listKey = [...leadsKey, "list"] as const;
export const detailKey = (id: string) => [...leadsKey, "detail", id] as const;
export const activityKey = (id: string) => [...leadsKey, "activity", id] as const;
export const auditsKey = (id: string) => [...leadsKey, "audits", id] as const;
export const useLeadsList = () =>
  useQuery({ queryKey: listKey, queryFn: leadsRepo.list });

/** Calls, messages and emails logged since the start of today (South Africa). */
export const useOutreachToday = (sinceMs: number) =>
  useQuery({
    queryKey: [...leadsKey, "outreach", sinceMs] as const,
    queryFn: () => leadsRepo.outreachSince(sinceMs),
  });

export const useLead = (id: string | undefined) =>
  useQuery({
    queryKey: detailKey(id ?? ""),
    queryFn: () => leadsRepo.get(id as string),
    enabled: Boolean(id),
  });

/** Every audit run saved on the lead (the Website audit tab's history). */
export const useLeadAudits = (id: string) =>
  useQuery({ queryKey: auditsKey(id), queryFn: () => leadsRepo.audits(id) });

export const useLeadActivity = (id: string | undefined) =>
  useQuery({
    queryKey: activityKey(id ?? ""),
    queryFn: () => leadsRepo.activity(id as string),
    enabled: Boolean(id),
  });

/**
 * The lead's saved report as a PDF, printed by the server without a new scan.
 * Kept for the session per audit, so opening the email twice prints it once.
 */
const auditPdfQuery = (leadId: string, auditId: string) => ({
  queryKey: [...leadsKey, "auditPdf", leadId, auditId] as const,
  queryFn: () => leadsRepo.auditPdf(leadId, auditId),
  staleTime: Infinity,
  retry: false,
});

export const useLeadAuditPdf = (leadId: string, auditId: string | undefined) =>
  useQuery({ ...auditPdfQuery(leadId, auditId ?? ""), enabled: Boolean(auditId) });

/** The same report on demand (a button), shared with the email's copy. */
export const useFetchLeadAuditPdf = () => {
  const queryClient = useQueryClient();
  return (leadId: string, auditId: string) => queryClient.fetchQuery(auditPdfQuery(leadId, auditId));
};
